import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useIsMobile } from '../../core/useIsMobile';

const COUNT_DESKTOP = 5200;
const COUNT_MOBILE = 2600;

const COL_WARM = [0xf5 / 255, 0xe8 / 255, 0xd0 / 255] as const;
const COL_CREAM = [0xdc / 255, 0xc9 / 255, 0xa8 / 255] as const;
const COL_GOLD = [0xc9 / 255, 0xa3 / 255, 0x4a / 255] as const;
const COL_GRAY = [0x8e / 255, 0x81 / 255, 0x70 / 255] as const;

const _inv = new THREE.Matrix4();
const _camLocal = new THREE.Vector3();
const _e1 = new THREE.Vector3();
const _e2 = new THREE.Vector3();
const _n = new THREE.Vector3();

function hash2(i: number, j: number) {
  const n = Math.sin(i * 127.1 + j * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

function makeDustSprite() {
  const s = 64;
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    const tex = new THREE.Texture();
    return tex;
  }
  const g = ctx.createRadialGradient(s * 0.5, s * 0.5, 0, s * 0.5, s * 0.5, s * 0.5);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.45, 'rgba(255,255,255,0.5)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, s, s);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

function pickGroup(i: number): 0 | 1 | 2 {
  const r = hash2(i, 9);
  if (r < 0.65) return 0;
  if (r < 0.9) return 1;
  return 2;
}

function pickSize(i: number, group: number, edge: boolean) {
  if (edge) return 0.008 + hash2(i, 21) * 0.008;
  if (group === 2) {
    const r = hash2(i, 20);
    if (r < 0.55) return 0.03 + hash2(i, 21) * 0.015;
    return 0.045 + hash2(i, 21) * 0.025;
  }
  const r = hash2(i, 20);
  if (r < 0.6) return 0.008 + hash2(i, 21) * 0.01;
  if (r < 0.85) return 0.018 + hash2(i, 21) * 0.012;
  if (r < 0.95) return 0.03 + hash2(i, 21) * 0.015;
  return 0.045 + hash2(i, 21) * 0.025;
}

function pickColor(i: number, size: number, out: Float32Array, o: number) {
  const r = hash2(i, 23);
  let c: readonly [number, number, number];
  if (r < 0.45) c = COL_WARM;
  else if (r < 0.75) c = COL_CREAM;
  else if (r < 0.9) c = COL_GOLD;
  else c = COL_GRAY;
  if (size < 0.016 && r < 0.75 && hash2(i, 22) < 0.04) c = COL_GOLD;
  out[o] = c[0];
  out[o + 1] = c[1];
  out[o + 2] = c[2];
}

function densityAllows(dissolve: number, rand: number) {
  if (dissolve < 0.2) return rand < 0.18;
  if (dissolve < 0.55) return rand < 0.88;
  if (dissolve < 0.8) return true;
  return rand < 0.28;
}

function sampleOnGeometry(geo: THREE.BufferGeometry, count: number) {
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const nrm = geo.attributes.normal as THREE.BufferAttribute | undefined;
  const idx = geo.index;
  const triCount = Math.max(1, idx ? idx.count / 3 : pos.count / 3);

  const positions = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const release = new Float32Array(count);
  const random = new Float32Array(count);
  const size = new Float32Array(count);
  const life = new Float32Array(count);
  const delay = new Float32Array(count);
  const opacity = new Float32Array(count);
  const angle = new Float32Array(count);
  const spin = new Float32Array(count);
  const turb = new Float32Array(count);
  const nspd = new Float32Array(count);
  const shimmer = new Float32Array(count);
  const group = new Uint8Array(count);
  const edge = new Uint8Array(count);

  let yMin = Infinity;
  let yMax = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    if (y < yMin) yMin = y;
    if (y > yMax) yMax = y;
  }
  const ySpan = Math.max(1e-4, yMax - yMin);
  const edgeStart = Math.floor(count * 0.82);

  for (let i = 0; i < count; i++) {
    const isEdge = i >= edgeStart;
    edge[i] = isEdge ? 1 : 0;
    const t = Math.floor(hash2(i, 1) * triCount) % triCount;
    let ia: number;
    let ib: number;
    let ic: number;
    if (idx) {
      ia = idx.getX(t * 3);
      ib = idx.getX(t * 3 + 1);
      ic = idx.getX(t * 3 + 2);
    } else {
      ia = t * 3;
      ib = t * 3 + 1;
      ic = t * 3 + 2;
    }

    let u = hash2(i, 2);
    let v = hash2(i, 3);
    if (u + v > 1) {
      u = 1 - u;
      v = 1 - v;
    }
    const w = 1 - u - v;
    const x = pos.getX(ia) * u + pos.getX(ib) * v + pos.getX(ic) * w;
    const y = pos.getY(ia) * u + pos.getY(ib) * v + pos.getY(ic) * w;
    const z = pos.getZ(ia) * u + pos.getZ(ib) * v + pos.getZ(ic) * w;

    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;

    if (nrm) {
      const nx = nrm.getX(ia) * u + nrm.getX(ib) * v + nrm.getX(ic) * w;
      const ny = nrm.getY(ia) * u + nrm.getY(ib) * v + nrm.getY(ic) * w;
      const nz = nrm.getZ(ia) * u + nrm.getZ(ib) * v + nrm.getZ(ic) * w;
      const len = Math.hypot(nx, ny, nz) || 1;
      normals[i * 3] = nx / len;
      normals[i * 3 + 1] = ny / len;
      normals[i * 3 + 2] = nz / len;
    } else {
      _e1.set(
        pos.getX(ib) - pos.getX(ia),
        pos.getY(ib) - pos.getY(ia),
        pos.getZ(ib) - pos.getZ(ia),
      );
      _e2.set(
        pos.getX(ic) - pos.getX(ia),
        pos.getY(ic) - pos.getY(ia),
        pos.getZ(ic) - pos.getZ(ia),
      );
      _n.copy(_e1).cross(_e2).normalize();
      normals[i * 3] = _n.x;
      normals[i * 3 + 1] = _n.y;
      normals[i * 3 + 2] = _n.z;
    }

    const yn = (y - yMin) / ySpan;
    random[i] = hash2(i, 4);
    const n = isEdge
      ? (hash2(i, 7) - 0.5) * 0.05
      : (hash2(i, 7) - 0.5) * 0.22;
    release[i] = THREE.MathUtils.clamp(yn + n, 0.02, 0.98);

    const g = isEdge ? 0 : pickGroup(i);
    group[i] = g;
    size[i] = pickSize(i, g, isEdge);
    pickColor(i, size[i], colors, i * 3);

    delay[i] = hash2(i, 24) * 0.2;
    opacity[i] = 0.72 + hash2(i, 25) * 0.28;
    angle[i] = hash2(i, 26) * Math.PI * 2;
    spin[i] = (hash2(i, 27) - 0.5) * 1.8;
    turb[i] = 0.08 + hash2(i, 28) * 0.1;
    nspd[i] = 0.25 + hash2(i, 29) * 0.2;
    shimmer[i] = hash2(i, 30) < 0.025 ? 1 : 0;

    if (isEdge) life[i] = 0.7 + hash2(i, 31) * 0.45;
    else if (g === 0) life[i] = 1.35 + hash2(i, 31) * 0.55;
    else if (g === 1) life[i] = 1.7 + hash2(i, 31) * 0.7;
    else life[i] = 1.15 + hash2(i, 31) * 0.55;
  }

  return {
    positions,
    normals,
    colors,
    release,
    random,
    size,
    life,
    delay,
    opacity,
    angle,
    spin,
    turb,
    nspd,
    shimmer,
    group,
    edge,
  };
}

/**
 * Cloth becomes a single Points cloud — one sprite, one geometry, one shader.
 */
export function JerseyDustParticles({
  geometry,
  dissolve,
  active,
}: {
  geometry: THREE.BufferGeometry;
  dissolve: number;
  active: boolean;
}) {
  const mobile = useIsMobile();
  const count = mobile ? COUNT_MOBILE : COUNT_DESKTOP;
  const points = useRef<THREE.Points>(null);
  const { camera } = useThree();
  const seed = useMemo(
    () => sampleOnGeometry(geometry, count),
    [geometry, count],
  );

  const buffers = useMemo(() => {
    const pos = new Float32Array(count * 3);
    pos.set(seed.positions);
    const age = new Float32Array(count);
    age.fill(-1);
    const alive = new Uint8Array(count);
    const vel = new Float32Array(count * 3);
    const wait = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const nx = seed.normals[i * 3];
      const ny = seed.normals[i * 3 + 1];
      const nz = seed.normals[i * 3 + 2];
      const g = seed.group[i];
      const yBias = -0.15 + hash2(i, 32) * 0.6;
      if (g === 0) {
        const spd = 0.15 + hash2(i, 14) * 0.3;
        vel[i * 3] = nx * spd + (hash2(i, 11) - 0.5) * 0.08;
        vel[i * 3 + 1] = yBias * 0.35 + ny * spd * 0.2;
        vel[i * 3 + 2] = nz * spd + (hash2(i, 13) - 0.5) * 0.08;
      } else if (g === 1) {
        const spd = 0.45 + hash2(i, 14) * 0.65;
        vel[i * 3] = (hash2(i, 11) - 0.5) * 2 * spd;
        vel[i * 3 + 1] = yBias;
        vel[i * 3 + 2] = (hash2(i, 13) - 0.5) * 2 * spd;
      } else {
        const spd = 0.8 + hash2(i, 14) * 0.8;
        vel[i * 3] = spd;
        vel[i * 3 + 1] = yBias * 0.25;
        vel[i * 3 + 2] = spd;
      }
    }
    return { pos, age, alive, vel, wait };
  }, [seed, count]);

  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(buffers.pos, 3));
    g.setAttribute('aAge', new THREE.BufferAttribute(new Float32Array(count), 1));
    g.setAttribute('aLife', new THREE.BufferAttribute(seed.life.slice(), 1));
    g.setAttribute('aSize', new THREE.BufferAttribute(seed.size.slice(), 1));
    g.setAttribute('aRand', new THREE.BufferAttribute(seed.random.slice(), 1));
    g.setAttribute('aOpacity', new THREE.BufferAttribute(seed.opacity.slice(), 1));
    g.setAttribute('aAngle', new THREE.BufferAttribute(seed.angle.slice(), 1));
    g.setAttribute('aSpin', new THREE.BufferAttribute(seed.spin.slice(), 1));
    g.setAttribute('aShimmer', new THREE.BufferAttribute(seed.shimmer.slice(), 1));
    g.setAttribute('aColor', new THREE.BufferAttribute(seed.colors.slice(), 3));
    g.setAttribute(
      'aSoft',
      new THREE.BufferAttribute(
        Float32Array.from({ length: count }, (_, i) =>
          seed.group[i] === 2 ? 1 : seed.edge[i] ? 0.35 : 0.15,
        ),
        1,
      ),
    );
    return g;
  }, [buffers, seed, count]);

  const sprite = useMemo(() => makeDustSprite(), []);

  const mat = useMemo(() => {
    return new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.NormalBlending,
      uniforms: {
        uMap: { value: sprite },
        uPixelRatio: { value: 1 },
        uHeight: { value: 800 },
        uTime: { value: 0 },
        uDissolve: { value: 0 },
      },
      vertexShader: /* glsl */ `
        attribute float aAge;
        attribute float aLife;
        attribute float aSize;
        attribute float aRand;
        attribute float aOpacity;
        attribute float aAngle;
        attribute float aSpin;
        attribute float aShimmer;
        attribute float aSoft;
        attribute vec3 aColor;
        uniform float uPixelRatio;
        uniform float uHeight;
        uniform float uTime;
        uniform float uDissolve;
        varying float vAlpha;
        varying float vAngle;
        varying float vSoft;
        varying vec3 vColor;
        void main() {
          float alive = step(0.0, aAge);
          float life = max(aLife, 0.05);
          float t = clamp(aAge / life, 0.0, 1.0);
          float d = uDissolve;
          float env;
          if (d < 0.20) env = mix(0.28, 0.42, d / 0.20);
          else if (d < 0.55) env = mix(0.45, 0.68, (d - 0.20) / 0.35);
          else if (d < 0.80) env = mix(0.38, 0.18, (d - 0.55) / 0.25);
          else env = mix(0.16, 0.07, (d - 0.80) / 0.20);
          float birth = smoothstep(0.0, 0.07, t);
          float fade = 1.0 - smoothstep(0.72, 1.0, t);
          vAlpha = alive * env * birth * fade * aOpacity;
          vAngle = aAngle + uTime * aSpin;
          vSoft = aSoft;
          vec3 gold = vec3(0.788, 0.639, 0.290);
          float shimmer = aShimmer * sin(uTime * 2.0 + aRand * 6.28318) * 0.08;
          vColor = mix(aColor, gold, aShimmer * (0.5 + 0.5 * sin(uTime * 2.0 + aRand * 12.0)));
          vColor *= (1.0 + shimmer);
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mv;
          float dist = max(0.42, -mv.z);
          float px = aSize * (uHeight * 0.52) / dist;
          px *= mix(1.0, 0.72, t);
          gl_PointSize = max(1.2, px * uPixelRatio) * alive;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D uMap;
        varying float vAlpha;
        varying float vAngle;
        varying float vSoft;
        varying vec3 vColor;
        void main() {
          float cs = cos(vAngle);
          float sn = sin(vAngle);
          vec2 c = gl_PointCoord - 0.5;
          vec2 uv = vec2(c.x * cs - c.y * sn, c.x * sn + c.y * cs) + 0.5;
          float mask = texture2D(uMap, uv).a;
          float d = length(c) * 2.0;
          float radial = 1.0 - smoothstep(mix(0.35, 0.12, 1.0 - vSoft), 1.0, d);
          float alpha = vAlpha * mask * mix(1.0, radial, 0.55);
          if (alpha < 0.004) discard;
          gl_FragColor = vec4(vColor, alpha);
        }
      `,
    });
  }, [sprite]);

  useEffect(
    () => () => {
      geo.dispose();
      mat.dispose();
      sprite.dispose();
    },
    [geo, mat, sprite],
  );

  useEffect(() => {
    if (dissolve > 0.002) return;
    buffers.age.fill(-1);
    buffers.alive.fill(0);
    buffers.wait.fill(0);
    buffers.pos.set(seed.positions);
    const ageAttr = geo.getAttribute('aAge') as THREE.BufferAttribute;
    ageAttr.array.fill(-1);
    ageAttr.needsUpdate = true;
    (geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }, [dissolve <= 0.002, active, buffers, seed, geo]);

  useFrame(({ gl, size }, dt) => {
    mat.uniforms.uPixelRatio.value = Math.min(2, gl.getPixelRatio());
    mat.uniforms.uHeight.value = size.height;
    mat.uniforms.uTime.value += dt;
    mat.uniforms.uDissolve.value = dissolve;
    if (!active && dissolve <= 0) return;

    const parent = points.current;
    if (parent) {
      parent.updateWorldMatrix(true, false);
      _inv.copy(parent.matrixWorld).invert();
      _camLocal.copy(camera.position).applyMatrix4(_inv);
    }

    const { pos, age, alive, vel, wait } = buffers;
    const ageArr = (geo.getAttribute('aAge') as THREE.BufferAttribute)
      .array as Float32Array;
    const posAttr = geo.getAttribute('position') as THREE.BufferAttribute;
    const ageAttr = geo.getAttribute('aAge') as THREE.BufferAttribute;
    let dirty = false;
    const step = Math.min(dt, 0.04);

    for (let i = 0; i < count; i++) {
      if (alive[i] === 0 && dissolve >= seed.release[i]) {
        if (!densityAllows(dissolve, seed.random[i])) continue;
        wait[i] += step;
        if (wait[i] < seed.delay[i]) continue;
        alive[i] = 1;
        age[i] = 0;
        wait[i] = 0;
        pos[i * 3] = seed.positions[i * 3];
        pos[i * 3 + 1] = seed.positions[i * 3 + 1];
        pos[i * 3 + 2] = seed.positions[i * 3 + 2];
        dirty = true;
      }
      if (alive[i] === 0) continue;

      const life = seed.life[i];
      age[i] += step;
      if (age[i] >= life) {
        alive[i] = 0;
        age[i] = -1;
        ageArr[i] = -1;
        dirty = true;
        continue;
      }

      const u = age[i] / life;
      const g = seed.group[i];
      let vx = vel[i * 3];
      let vy = vel[i * 3 + 1];
      let vz = vel[i * 3 + 2];

      if (g === 2) {
        const px = pos[i * 3];
        const py = pos[i * 3 + 1];
        const pz = pos[i * 3 + 2];
        let tx = _camLocal.x - px;
        let ty = _camLocal.y - py;
        let tz = _camLocal.z - pz;
        const tl = Math.hypot(tx, ty, tz) || 1;
        const spd = vel[i * 3];
        vx = (tx / tl) * spd;
        vy = (ty / tl) * spd * 0.85 + vel[i * 3 + 1];
        vz = (tz / tl) * spd;
      }

      const boost = g === 0 ? 0.75 + u * 0.45 : g === 1 ? 0.95 + u * 0.85 : 1.05 + u * 0.4;
      const ns = seed.nspd[i];
      const ts = seed.turb[i] * (1.0 - u * 0.35);
      const swirlX = Math.sin(age[i] * ns * 6.28 + i * 0.17) * ts;
      const swirlY = Math.cos(age[i] * ns * 5.1 + i * 0.13) * ts * 0.65;
      const swirlZ = Math.sin(age[i] * ns * 4.7 + i * 0.21) * ts;

      pos[i * 3] += vx * boost * step + swirlX * step;
      pos[i * 3 + 1] += vy * boost * step + swirlY * step;
      pos[i * 3 + 2] += vz * boost * step + swirlZ * step;

      ageArr[i] = age[i];
      dirty = true;
    }

    if (dirty) {
      posAttr.needsUpdate = true;
      ageAttr.needsUpdate = true;
    }
  });

  if (!active && dissolve <= 0) return null;

  return (
    <points ref={points} geometry={geo} material={mat} frustumCulled={false} />
  );
}

/** Rising noisy clip for MeshStandardMaterial — bottom vanishes first. */
export function applyClothDissolveShader(
  mat: THREE.MeshStandardMaterial,
  uniforms: {
    uDissolve: { value: number };
    uYMin: { value: number };
    uYMax: { value: number };
    /** When 1, clip by uFixedY (Decal helper space ≠ shirt Y). */
    uUseFixedY?: { value: number };
    uFixedY?: { value: number };
  },
) {
  // Dissolve uses discard — keep caller in charge of transparent/depthWrite.
  const uUseFixedY = uniforms.uUseFixedY ?? { value: 0 };
  const uFixedY = uniforms.uFixedY ?? { value: 0 };
  uniforms.uUseFixedY = uUseFixedY;
  uniforms.uFixedY = uFixedY;

  const prev = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader, renderer) => {
    prev?.(shader, renderer);
    shader.uniforms.uDissolve = uniforms.uDissolve;
    shader.uniforms.uYMin = uniforms.uYMin;
    shader.uniforms.uYMax = uniforms.uYMax;
    shader.uniforms.uUseFixedY = uUseFixedY;
    shader.uniforms.uFixedY = uFixedY;
    shader.vertexShader = shader.vertexShader.replace(
      '#include <common>',
      /* glsl */ `
      #include <common>
      varying float vLocalY;
      `,
    );
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      /* glsl */ `
      #include <begin_vertex>
      vLocalY = position.y;
      `,
    );
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      /* glsl */ `
      #include <common>
      varying float vLocalY;
      uniform float uDissolve;
      uniform float uYMin;
      uniform float uYMax;
      uniform float uUseFixedY;
      uniform float uFixedY;
      float dissolveNoise(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }
      `,
    );
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <clipping_planes_fragment>',
      /* glsl */ `
      #include <clipping_planes_fragment>
      // Rising dissolve — same edge for cloth and name/number Decals
      if (uDissolve > 0.001) {
        float span = max(0.0001, uYMax - uYMin);
        float ySample = uUseFixedY > 0.5 ? uFixedY : vLocalY;
        float yn = clamp((ySample - uYMin) / span, 0.0, 1.0);
        float n = dissolveNoise(gl_FragCoord.xy * 0.22) * 0.12
                + dissolveNoise(vec2(ySample * 12.0, gl_FragCoord.x * 0.04)) * 0.08;
        float edge = uDissolve + n * uDissolve * 0.85;
        if (yn < edge) discard;
      }
      `,
    );
  };
  mat.needsUpdate = true;
  return uniforms;
}
